import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';
import type { FinancePeriod } from './use-finance-summary.js';

export interface PayrollDistribution {
  category: string;
  label: string;
  amount: number;
  headcount: number;
}

export interface PayrollTopPaid {
  name: string;
  driver_id?: string;
  terminal?: string;
  total_pay: number;
  category?: string;
}

export interface PayrollPayVsStops {
  driver_id: string;
  name: string;
  terminal?: string | null;
  pay: number | null;
  stops: number | null;
}

export interface PayrollDetailRow {
  name: string;
  driver_id?: string;
  terminal?: string;
  rate: number;
  rate_type?: string;
  days: number;
  hours: number;
  base_pay: number;
  vacation_pay: number;
  pto_pay: number;
  bonus: number;
  holiday_pay: number;
  perdiem: number;
  miles_amt: number;
  stop_incentive: number;
  peak_bonus: number;
  reimbursement: number;
  total_pay: number;
  employee_id?: string | null;
  dir_rate?: number | null;
  dir_rate_type?: string | null;
  exclude_from_efficiency?: boolean;
  role?: string | null;
}

export interface PayrollWeeklyTrend {
  week_ending: string;
  total: number;
}

export interface PayrollPayRate {
  id?: string | number;
  terminal?: string;
  rate_name?: string;
  rate_type?: string;
  rate_amount?: number;
  effective_date?: string;
}

export interface PayrollReportData {
  period: FinancePeriod;
  distribution: PayrollDistribution[];
  total_payroll: number;
  top_paid: PayrollTopPaid[];
  pay_vs_stops: PayrollPayVsStops[];
  detail_by_category: Record<string, PayrollDetailRow[]>;
  pay_rates: PayrollPayRate[];
  weekly_trend: PayrollWeeklyTrend[];
}

interface PayrollResponse extends PayrollReportData {
  error?: string;
}

export function useFinancePayroll(periodType: string) {
  const [data, setData] = useState<PayrollReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (periodType) params.set('period', periodType);

      const resp = await apiFetch<PayrollResponse>(`/api/finance/payroll?${params.toString()}`);
      if (resp?.error) throw new Error(resp.error);

      setData(resp);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Payroll report load failed');
    } finally {
      setLoading(false);
    }
  }, [periodType]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refreshPayroll: load };
}
